/**
 * 🔍 ProblemDetailPanel — MASP structured problem detail view
 *
 * Slide-in panel showing:
 * - Issue header (title, severity, mode, status)
 * - Stage timeline (8 MASP stages)
 * - Root cause analysis (embedded)
 * - Linked actions list
 * - Quick action buttons (advance stage, assign, etc.)
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, ChevronRight, User, Calendar, Clock, LinkIcon, Building2,
  AlertCircle, Search, Crosshair, ClipboardCheck, PlayCircle, Shield, Eye, CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { EnterpriseBadge } from '../layout/EnterpriseTable';
import { RootCauseAnalysisPanel } from './RootCauseAnalysisPanel';
import { MonitoringPanel } from './MonitoringPanel';
import { ContextualKPI } from '../analytics/ContextualKPI';
import { IssueService } from '../../services/issueService';
import {
  ISSUE_STATUS_LABELS, ISSUE_SEVERITY_LABELS, ISSUE_TYPE_LABELS,
  ISSUE_MODE_LABELS, getStatusTransitions, getStageOrder,
} from '../../types/issue';
import type { Issue, IssueStatus, RootCauseAnalysis, ValidationConfig, MonitoringConfig } from '../../types/issue';

interface ProblemDetailPanelProps {
  issue: Issue;
  organizationId: string;
  onClose: () => void;
  onRefresh: () => void;
  onToast: (type: 'success' | 'error' | 'warning' | 'info', msg: string) => void;
}

const STAGE_ICONS: Record<IssueStatus, React.FC<{ size?: number; className?: string }>> = {
  open: AlertCircle,
  investigation: Search,
  root_cause: Crosshair,
  action_plan: ClipboardCheck,
  execution: PlayCircle,
  validation: Shield,
  monitoring: Eye,
  closed: CheckCircle2,
};


const SEVERITY_COLOR: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
};

export const ProblemDetailPanel: React.FC<ProblemDetailPanelProps> = ({
  issue,
  organizationId,
  onClose,
  onRefresh,
  onToast,
}) => {
  const navigate = useNavigate();
  const [showRootCause, setShowRootCause] = useState(false);
  const [rcaSaving, setRcaSaving] = useState(false);
  const [rcaData, setRcaData] = useState<RootCauseAnalysis | null>(
    issue.root_cause_analysis ?? null,
  );

  const currentStageIdx = getStageOrder(issue.mode).indexOf(issue.status);
  const daysSinceCreation = Math.floor(
    (Date.now() - new Date(issue.created_at).getTime()) / (1000 * 60 * 60 * 24),
  );

  // SLA thresholds (days) by severity
  const SLA_DAYS: Record<string, number> = { critical: 3, high: 7, medium: 14, low: 30 };
  const slaDays = SLA_DAYS[issue.severity] ?? 14;
  const slaRemaining = slaDays - daysSinceCreation;
  const slaBusted = slaRemaining < 0;
  const slaWarning = slaRemaining >= 0 && slaRemaining <= 2;

  // Next stage (forward)
  const nextForwardStage = useMemo(() => {
    const allowed = getStatusTransitions(issue.mode)[issue.status] ?? [];
    return allowed.find(s => getStageOrder(issue.mode).indexOf(s) > currentStageIdx) ?? null;
  }, [issue.status, currentStageIdx]);

  const handleTransition = useCallback(async (newStatus: IssueStatus) => {
    try {
      await IssueService.transitionStatus(organizationId, issue.id, newStatus);
      onToast('success', `Status alterado para ${ISSUE_STATUS_LABELS[newStatus]}`);
      onRefresh();
    } catch (err) {
      console.error('[ProblemDetail] transition failed:', err);
      onToast('error', 'Transição de status não permitida');
    }
  }, [organizationId, issue.id, onRefresh, onToast]);

  const handleSaveRCA = useCallback(async (analysis: RootCauseAnalysis) => {
    setRcaSaving(true);
    try {
      await IssueService.saveRootCauseAnalysis(organizationId, issue.id, analysis);
      onToast('success', 'Análise de causa raiz salva');
      onRefresh();
    } catch (err) {
      console.error('[ProblemDetail] save RCA failed:', err);
      onToast('error', 'Erro ao salvar análise');
    } finally {
      setRcaSaving(false);
    }
  }, [organizationId, issue.id, onRefresh, onToast]);

  const handleSaveValidation = useCallback(async (config: ValidationConfig) => {
    try {
      await IssueService.saveValidationConfig(organizationId, issue.id, config);
      onToast('success', 'Configuração de validação salva');
      onRefresh();
    } catch (err) {
      console.error('[ProblemDetail] save validation failed:', err);
      onToast('error', 'Erro ao salvar validação');
    }
  }, [organizationId, issue.id, onRefresh, onToast]);

  const handleSaveMonitoring = useCallback(async (config: MonitoringConfig) => {
    try {
      await IssueService.saveMonitoringConfig(organizationId, issue.id, config);
      onToast('success', 'Configuração de monitoramento salva');
      onRefresh();
    } catch (err) {
      console.error('[ProblemDetail] save monitoring failed:', err);
      onToast('error', 'Erro ao salvar monitoramento');
    }
  }, [organizationId, issue.id, onRefresh, onToast]);

  const handleAddInspection = useCallback(async (inspection: { inspection_date: string; notes: string }) => {
    try {
      await IssueService.addManualInspection(organizationId, issue.id, inspection);
      onToast('success', 'Inspeção registrada');
      onRefresh();
    } catch (err) {
      console.error('[ProblemDetail] add inspection failed:', err);
      onToast('error', 'Erro ao registrar inspeção');
    }
  }, [organizationId, issue.id, onRefresh, onToast]);

  const handleCompleteValidation = useCallback(async (result: 'passed' | 'failed') => {
    try {
      await IssueService.completeValidation(organizationId, issue.id, result);
      onToast('success', result === 'passed' ? 'Validação aprovada' : 'Validação reprovada');
      onRefresh();
    } catch (err) {
      console.error('[ProblemDetail] complete validation failed:', err);
      onToast('error', 'Erro ao concluir validação');
    }
  }, [organizationId, issue.id, onRefresh, onToast]);

  const handleCreateAction = useCallback(() => {
    navigate('/acoes/nova', {
      state: {
        fromIssue: {
          id: issue.id,
          title: issue.title,
          severity: issue.severity,
          description: issue.description,
        },
      },
    });
  }, [navigate, issue]);

  return (
    <div className={`flex h-full w-[420px] flex-col border-l bg-white dark:bg-slate-900 ${
      slaBusted 
        ? 'border-amber-500/50 shadow-[inset_3px_0_0_rgba(245,158,11,0.6)] dark:shadow-[inset_3px_0_0_rgba(245,158,11,0.4)]'
        : 'border-slate-200 dark:border-slate-700'
    }`}>
      {/* Header */}
      <div className="flex flex-col gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 leading-snug">
              {issue.title}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <EnterpriseBadge label={ISSUE_SEVERITY_LABELS[issue.severity]} color={SEVERITY_COLOR[issue.severity]} />
              <EnterpriseBadge
                label={ISSUE_MODE_LABELS[issue.mode ?? 'quick_correction']}
                color={issue.mode === 'structured_problem'
                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'}
              />
              <span className="text-[11px] font-semibold tracking-wide text-slate-400 dark:text-slate-500 ml-1">
                • {ISSUE_TYPE_LABELS[issue.type]}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Compact Metadata Header */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] font-medium text-slate-600 dark:text-slate-400">
          <div className="flex items-center gap-1.5" title="Responsável">
            <User size={13} className="text-slate-400" />
            <span className={issue.assigned_to ? '' : 'text-amber-600 italic'}>
              {issue.assigned_to ?? 'Não atribuído'}
            </span>
          </div>
          <div className="flex items-center gap-1.5" title="Departamento">
            <Building2 size={13} className="text-slate-400" />
            <span>{issue.department ?? '-'}</span>
          </div>
          <div className="flex items-center gap-1.5" title="Abertura / SLA">
            <Clock size={13} className={slaBusted ? 'text-amber-500' : 'text-slate-400'} />
            <span className={slaBusted ? 'text-amber-600 font-bold' : ''}>
              {daysSinceCreation} / {slaDays} dias
            </span>
          </div>
        </div>
      </div>

      {/* Content — scrollable */}
      <div className="flex-1 overflow-y-auto p-6 space-y-7">
        {/* ── Stage Timeline ───────────────────────── */}
        <section>
          <div className="flex items-center justify-between mb-5 px-1">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Stepping Progresso
            </h3>
            <span className="text-[11px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2.5 py-1 rounded">
              {ISSUE_STATUS_LABELS[issue.status]}
            </span>
          </div>
          
          <div className="relative flex items-center justify-between w-full h-14">
            {/* Background Line */}
            <div className="absolute top-4 left-4 right-4 h-[2px] bg-slate-200 dark:bg-slate-800 z-0" />
            
            {getStageOrder(issue.mode).map((stage, idx, arr) => {
              const Icon = STAGE_ICONS[stage];
              const isClosed = issue.status === 'closed';
              const isCurrent = !isClosed && stage === issue.status;
              const isPast = isClosed ? true : idx < currentStageIdx;
              
              return (
                <div key={stage} className="relative z-10 flex flex-col items-center gap-2 flex-1 group">
                  <div
                    title={ISSUE_STATUS_LABELS[stage]}
                    className={`flex h-[34px] w-[34px] items-center justify-center rounded-full border-[2.5px] transition-all duration-300 ${
                      isCurrent
                        ? 'border-blue-500 bg-white text-blue-600 dark:border-blue-400 dark:bg-slate-900 dark:text-blue-400 scale-110 shadow-md shadow-blue-500/20 relative z-20'
                        : isPast
                          ? 'border-emerald-500 bg-emerald-500 text-white dark:border-emerald-500 dark:bg-emerald-500'
                          : 'border-slate-200 bg-white text-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-600'
                    }`}
                  >
                    {isPast ? <CheckCircle2 size={16} strokeWidth={2.5} /> : <Icon size={15} strokeWidth={2} />}
                  </div>
                  <span className={`hidden sm:block text-[10px] leading-tight text-center font-semibold mt-1 ${
                    isCurrent ? 'text-blue-700 dark:text-blue-400' 
                    : isPast ? 'text-slate-700 dark:text-slate-300' 
                    : 'text-slate-400 dark:text-slate-600'
                  }`}>
                    {ISSUE_STATUS_LABELS[stage]}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── SLA Pressure Bar ─────────────────────── */}
        {(slaWarning || slaBusted) && issue.status !== 'closed' && (
          <div className={`mt-2 rounded border p-4 shadow-sm ${
            slaBusted 
              ? 'bg-amber-500/10 border-amber-500/30 dark:bg-amber-900/20 dark:border-amber-500/20'
              : 'bg-yellow-500/10 border-yellow-500/30 dark:bg-yellow-900/20 dark:border-yellow-500/20'
          }`}>
            <div className={`flex items-start gap-3 ${slaBusted ? 'text-amber-800 dark:text-amber-400' : 'text-yellow-800 dark:text-yellow-400'}`}>
              <AlertTriangle size={20} className="mt-0.5 shrink-0" strokeWidth={2} />
              <div>
                <p className="text-[13px] font-bold leading-none mb-1.5">
                  {slaBusted ? 'SLA Estourado!' : 'Atenção ao Limite (SLA)'}
                </p>
                <p className="text-[11px] opacity-90 leading-relaxed font-medium">
                  {slaBusted 
                    ? `O prazo técnico desta tratativa (${slaDays} dias) já foi vencido em ${Math.abs(slaRemaining)} dia(s).`
                    : `Restam apenas ${slaRemaining} dia(s) para estourar o limite de ${slaDays} dias dessa classificação.`}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="h-px w-full bg-slate-200/60 dark:bg-slate-800/60" />

        {/* ── Description ──────────────────────────── */}
        {issue.description && (
          <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Descrição
            </h3>
            <p className="text-[11px] leading-relaxed text-slate-700 dark:text-slate-300">
              {issue.description}
            </p>
          </div>
        )}

        {/* ── Root Cause Analysis ──────────────────── */}
        {(issue.mode === 'structured_problem') && (
          <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <button
              type="button"
              onClick={() => setShowRootCause(prev => !prev)}
              className="mb-2 flex w-full items-center gap-1.5 text-left"
            >
              <Crosshair size={12} className={showRootCause ? 'text-blue-500' : 'text-slate-500 dark:text-slate-400'} />
              <h3 className={`text-[10px] font-bold uppercase tracking-wider ${showRootCause ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`}>
                Análise de Causa Raiz
              </h3>
              <ChevronRight
                size={12}
                className={`ml-auto transition-transform ${showRootCause ? 'rotate-90 text-blue-500' : 'text-slate-400'}`}
              />
            </button>
            {showRootCause && (
              <RootCauseAnalysisPanel
                value={rcaData}
                onChange={setRcaData}
                onSave={handleSaveRCA}
                saving={rcaSaving}
                readOnly={issue.status !== 'open' && issue.status !== 'investigation' && issue.status !== 'root_cause'}
                issueStatus={issue.status}
              />
            )}
          </div>
        )}

        {/* ── Validation & Monitoring ─────────────── */}
        {(issue.status === 'validation' || issue.status === 'monitoring' ||
          issue.validation_config != null || issue.monitoring_config != null) && (
          <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Validação & Monitoramento
            </h3>
            <MonitoringPanel
              validationConfig={issue.validation_config ?? null}
              monitoringConfig={issue.monitoring_config ?? null}
              issueStatus={issue.status}
              onSaveValidation={handleSaveValidation}
              onSaveMonitoring={handleSaveMonitoring}
              onAddInspection={handleAddInspection}
              onCompleteValidation={handleCompleteValidation}
              readOnly={issue.status === 'closed'}
            />
          </div>
        )}

        {/* ── Linked Actions ──────────────────────── */}
        <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <LinkIcon size={12} className="text-slate-500 dark:text-slate-400" />
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Ações Vinculadas
              </h3>
              {(issue.linked_actions_count ?? 0) > 0 && (
                <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[9px] font-bold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {issue.linked_actions_count}
                </span>
              )}
            </div>
          </div>
          {['action_plan', 'execution', 'validation', 'monitoring'].includes(issue.status) && (
            <button
              type="button"
              onClick={handleCreateAction}
              className="mt-1 w-full rounded border border-dashed border-slate-300 py-2 text-[11px] font-medium text-slate-500 transition hover:border-blue-400 hover:text-blue-600 dark:border-slate-600 dark:text-slate-400"
            >
              + Criar Nova Ação
            </button>
          )}
          {(!['action_plan', 'execution', 'validation', 'monitoring'].includes(issue.status) && (issue.linked_actions_count ?? 0) === 0) && (
            <p className="text-[10px] text-slate-400 font-medium italic mt-2">
              Ações serão planejadas na etapa apropriada.
            </p>
          )}
        </div>

        {/* ── Resolution ───────────────────────────── */}
        {issue.resolution && (
          <div className="px-4 py-3">
            <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Resolução
            </h3>
            <p className="text-[11px] leading-relaxed text-slate-700 dark:text-slate-300">
              {issue.resolution}
            </p>
          </div>
        )}

        {/* ── Contextual KPI Gestão à Vista ───────────────────────────── */}
        {['validation', 'monitoring', 'closed'].includes(issue.status) && (
          <div className="px-4 py-4 mt-2 border-t border-slate-100 dark:border-slate-800/60 bg-slate-50/50 dark:bg-slate-900/30">
            <h3 className="mb-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Validação de Eficácia (Gestão à Vista)
            </h3>
            <div className="scale-95 origin-top">
              <ContextualKPI 
                issueId={issue.id} 
                organizationId={organizationId}
                layout="sidebar" 
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Footer: Primary Action CTA ────────────── */}
      <div className="border-t border-slate-200 bg-white dark:bg-slate-900 p-5 dark:border-slate-800 shadow-[0_-8px_20px_-5px_rgba(0,0,0,0.08)] dark:shadow-[0_-8px_20px_-5px_rgba(0,0,0,0.4)] shrink-0 z-20">
        {nextForwardStage ? (
          <div className="flex flex-col gap-3">
            <div className="flex justify-between items-center text-[11px] text-slate-500 dark:text-slate-400 font-semibold px-1">
              <span className="flex items-center gap-1.5 uppercase tracking-wide">
                <span><span className="opacity-70">De:</span> <b className="text-slate-700 dark:text-slate-300">{ISSUE_STATUS_LABELS[issue.status]}</b></span>
              </span>
              <ChevronRight size={14} className="opacity-40" />
              <span className="flex items-center gap-1.5 uppercase tracking-wide">
                <span className="opacity-70">Para:</span> <b className="text-blue-600 dark:text-blue-500">{ISSUE_STATUS_LABELS[nextForwardStage]}</b>
              </span>
            </div>
            <button
              type="button"
              onClick={() => handleTransition(nextForwardStage)}
              className="flex w-full items-center justify-center gap-2 rounded bg-blue-600 px-4 py-3.5 text-[14px] font-bold text-white transition-all hover:bg-blue-700 hover:shadow-lg hover:-translate-y-0.5"
            >
              Avançar para Etapa 
              <ChevronRight size={18} strokeWidth={2.5} />
            </button>
          </div>
        ) : issue.status === 'closed' ? (
          <div className="flex justify-center items-center h-12 w-full text-[13px] font-bold tracking-wide text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 rounded border border-emerald-200 dark:border-emerald-800/50">
            <CheckCircle2 size={18} strokeWidth={2.5} className="mr-2.5 inline" />
            Problema encerrado
          </div>
        ) : (
          <div className="flex justify-center items-center h-12 w-full text-[12px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800/50 rounded">
            Nenhuma transição disponível
          </div>
        )}
      </div>
    </div>
  );
};

export default ProblemDetailPanel;
